// The Web ORCA product tour — a small state machine over the real app.
//
//   show ──Next/Back──▶ moving ──target ready──▶ show
//
// moving: the spotlight closes to a point (the page is dimmed and softly veiled,
// so a section that is still building is never seen), the card stays where it
// is with its buttons paused; the SPA opens the next section. The step becomes
// visible only when the target is really ready: right route, element present,
// no skeleton / busy state inside, its finite animations finished and its box
// stable for several frames. Then the page is scrolled under the veil and the
// light opens on the minimal meaningful element; the card moves next to it.
// No blind timeouts: readiness is read from the DOM every animation frame.
// While a step is read, the next section's code and data are prefetched.
// Plan-aware: a feature the plan does not open is shown as its locked preview.
// Completion is account-scoped (web/preferences); Skip counts as done; Settings
// → «Повторить знакомство» starts it again.
import { useCallback, useEffect, useLayoutEffect, useRef, useState, type CSSProperties } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router';
import { useT } from '@/i18n';
import { aiApi, api } from '@/lib/api/client';
import { MOCK_DATA } from '@/lib/api/mode';
import { preloadRoute } from '@/app/routes';
import { useAccount } from '@/hooks/queries';
import { useCaps, lockLine } from '@/hooks/useCaps';
import type { Feature } from '@/lib/features';
import { cx } from '@/lib/format';

export const WEB_TOUR_EVENT = 'orca:web-tour';
/** opens / closes the header's notification menu for the tour */
export const TOUR_BELL_EVENT = 'orca:tour-bell';

type StepDef = { key: string; path?: string; targets?: string[]; union?: boolean; feature?: Feature; bell?: boolean };
// left to right through the main navigation: Summary → Markets → Calendar → My assets → Orca AI → account
const STEPS: StepDef[] = [
  { key: 'summary', path: '/', targets: ['.hero .h-metric'] },
  { key: 'news', path: '/news', targets: ['.blk.feed .nfeat', '.blk.feed .ngrid > *', '.blk.feed .nlist > *'] },
  { key: 'calendar', path: '/calendar', feature: 'economic_calendar', targets: ['.calx-card', '.blk.calv'] },
  { key: 'portfolio', path: '/portfolio', targets: ['.hero .tile-add', '.pf-empty-hero .xbtn'] },
  { key: 'signals', path: '/signals', targets: ['.mobile-timeline', '.focus-body .timeline', '.blk.focus'] },
  // the switch and the first setting (coins / networks), not the whole settings column
  { key: 'alerts', path: '/alerts', feature: 'price_alerts', targets: ['#settings .wset-main', '#settings .wset-sec'], union: true },
  { key: 'whales', path: '/whales', feature: 'whale_alerts', targets: ['#settings .wset-main', '#settings .wset-sec'], union: true },
  { key: 'ai', path: '/ai', targets: ['.cs-cmp:not(.ask-cmp)', '.composer'] },
  { key: 'notices', path: '/settings', bell: true, targets: ['.app-top-shell .bn-panel'] },
  { key: 'settings', path: '/settings', targets: ['.hero .h-right .tiles'] },
  { key: 'plans', path: '/profile', targets: ['[data-tour="plan-current"]'] },
  { key: 'ready' },
];
/** a locked section: its title, what it is and the plan offer — one block */
const LOCKED_TARGETS = ['.hero .h-title-row', '.hero .h-intro', '.hero .h-metric'];
/** the data a section shows first (prefetched while the previous step is read) */
const PREFETCH: Record<string, { key: string[]; fn: () => unknown; feature?: Feature }> = {
  '/calendar': { key: ['calendar'], fn: api.calendar, feature: 'economic_calendar' },
  '/portfolio': { key: ['portfolio'], fn: api.portfolio },
  '/signals': { key: ['signals'], fn: api.signals },
  '/alerts': { key: ['alerts'], fn: api.alerts, feature: 'price_alerts' },
  '/whales': { key: ['alerts'], fn: api.alerts, feature: 'whale_alerts' },
  '/profile': { key: ['profile'], fn: api.profile },
};
const MOBILE = '(max-width: 600px)';
const PAD = 10;
const GIVE_UP_MS = 9000; // a section that never renders: the step is shown without a light

type Rect = { left: number; top: number; width: number; height: number };
type Phase = 'show' | 'moving';
const rectOf = (els: HTMLElement[]): Rect | null => {
  const rs = els.map((e) => e.getBoundingClientRect()).filter((r) => r.width > 2 && r.height > 2);
  if (!rs.length) return null;
  const left = Math.min(...rs.map((r) => r.left)), top = Math.min(...rs.map((r) => r.top));
  return { left, top, width: Math.max(...rs.map((r) => r.right)) - left, height: Math.max(...rs.map((r) => r.bottom)) - top };
};
const same = (a: Rect | null, b: Rect | null) => !!a && !!b && Math.abs(a.left - b.left) < 0.5 && Math.abs(a.top - b.top) < 0.5 && Math.abs(a.width - b.width) < 0.5 && Math.abs(a.height - b.height) < 0.5;
const visible = (el: Element) => {
  const r = el.getBoundingClientRect();
  return r.width > 4 && r.height > 4 && r.right > 8 && r.left < window.innerWidth - 8 && getComputedStyle(el).visibility !== 'hidden';
};
/** the step's elements: the first selector that matches (or, for a union, every selector) */
function findTargets(selectors: string[], union: boolean): HTMLElement[] {
  if (union) {
    const els = selectors.map((s) => [...document.querySelectorAll<HTMLElement>(s)].find(visible)).filter((e): e is HTMLElement => !!e);
    return els.length === selectors.length ? els : [];
  }
  for (const s of selectors) {
    const el = [...document.querySelectorAll<HTMLElement>(s)].find(visible);
    if (el) return [el];
  }
  return [];
}
/** settled: nothing loading inside, no finite animation still moving the element itself */
const settled = (els: HTMLElement[]) => els.every((el) =>
  !el.querySelector('.skel, [aria-busy="true"]') && el.getAttribute('aria-busy') !== 'true'
  && el.getAnimations().every((a) => a.playState !== 'running' || a.effect?.getComputedTiming().iterations === Infinity));
const frame = (): Promise<void> => new Promise((r) => { requestAnimationFrame(() => r()); });

export function WebTour() {
  const { t } = useT('onboarding');
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const caps = useCaps();
  const { plan } = useAccount();
  const prefs = useQuery({ queryKey: ['web-preferences'], queryFn: aiApi.preferences, enabled: !MOCK_DATA, staleTime: Infinity });
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);          // the step being shown (or the one we are leaving)
  const [want, setWant] = useState(0);          // the step being prepared
  const [phase, setPhase] = useState<Phase>('moving');
  const [slow, setSlow] = useState(false);
  const [rect, setRect] = useState<Rect | null>(null);
  const [mobile, setMobile] = useState(() => window.matchMedia(MOBILE).matches);
  const card = useRef<HTMLElement>(null);
  const [cardSize, setCardSize] = useState({ w: 360, h: 220 });
  const [dockH, setDockH] = useState(170);
  const startPath = useRef('/');
  const targets = useRef<HTMLElement[]>([]);
  const lastPlace = useRef<{ style?: CSSProperties; dock: boolean; topDock: boolean; center: boolean }>({ dock: false, topDock: false, center: true });
  const pathRef = useRef(pathname);
  pathRef.current = pathname;

  const lockedOf = useCallback((d: StepDef) => !!d.feature && !caps.can(d.feature), [caps]);
  const def = STEPS[step];
  const locked = lockedOf(def);
  const last = step === STEPS.length - 1;

  const start = useCallback(() => { startPath.current = window.location.pathname; setStep(0); setWant(0); setPhase('moving'); setRect(null); setOpen(true); }, []);
  useEffect(() => { if (prefs.data?.should_show_tour) start(); }, [prefs.data?.should_show_tour, start]);
  useEffect(() => {
    window.addEventListener(WEB_TOUR_EVENT, start);
    return () => window.removeEventListener(WEB_TOUR_EVENT, start);
  }, [start]);
  useEffect(() => {
    const mq = window.matchMedia(MOBILE);
    const on = () => setMobile(mq.matches);
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, []);

  const finish = useCallback(async (to: string) => {
    setOpen(false);
    window.dispatchEvent(new CustomEvent(TOUR_BELL_EVENT, { detail: false }));
    if (window.location.pathname !== to) navigate(to);
    if (!MOCK_DATA) {
      try { qc.setQueryData(['web-preferences'], await aiApi.completeTour()); } catch { /* completion is retried after the next tour */ }
    }
  }, [navigate, qc]);

  const go = useCallback((n: number) => { setWant(n); setPhase('moving'); }, []);

  // the transition: open the section, wait until the target is really ready, then show the step
  useEffect(() => {
    if (!open || phase !== 'moving') return;
    const d = STEPS[want];
    let alive = true;
    const slowTimer = window.setTimeout(() => alive && setSlow(true), 320);
    window.dispatchEvent(new CustomEvent(TOUR_BELL_EVENT, { detail: false }));
    if (d.path && window.location.pathname !== d.path) navigate(d.path);
    (async () => {
      await frame(); // the closing light and the veil start before anything moves
      const began = performance.now();
      const isLocked = lockedOf(d);
      const sel = isLocked ? LOCKED_TARGETS : d.targets;
      let els: HTMLElement[] = [];
      if (sel) {
        let prev: Rect | null = null, stable = 0;
        while (alive && performance.now() - began < GIVE_UP_MS) {
          await frame();
          if (d.path && pathRef.current !== d.path) { stable = 0; continue; }
          const found = findTargets(sel, isLocked || !!d.union);
          if (d.bell && !found.length) window.dispatchEvent(new CustomEvent(TOUR_BELL_EVENT, { detail: true }));
          const r = found.length ? rectOf(found) : null;
          if (!r || !settled(found)) { prev = null; stable = 0; continue; }
          stable = same(prev, r) ? stable + 1 : 0;
          prev = r;
          if (stable >= 3) { els = found; break; }
        }
        if (!alive) return;
        if (els.length) {
          // under the veil: bring the element to a comfortable place, instantly
          const r = rectOf(els)!;
          const topGap = mobile ? 72 : 96;
          const room = window.innerHeight - (mobile ? dockH + 24 : 24) - topGap;
          if (r.top < topGap || r.top + Math.min(r.height, room) > topGap + room) {
            const y = window.scrollY + r.top - topGap - (r.height < room && !mobile ? (room - r.height) / 2 : 0);
            window.scrollTo({ top: Math.max(0, y), behavior: 'instant' as ScrollBehavior });
          }
          let p2: Rect | null = null, s2 = 0;
          for (let i = 0; i < 40 && alive && s2 < 2; i += 1) { await frame(); const q = rectOf(els); s2 = same(p2, q) ? s2 + 1 : 0; p2 = q; }
        }
      }
      if (!alive) return;
      targets.current = els;
      setRect(els.length ? rectOf(els) : null);
      setStep(want);
      setSlow(false);
      setPhase('show');
    })();
    return () => { alive = false; window.clearTimeout(slowTimer); };
  }, [open, phase, want]); // eslint-disable-line react-hooks/exhaustive-deps

  // while a step is shown, the light follows its element (resize, rotation, a late image)
  useEffect(() => {
    if (!open || phase !== 'show' || !targets.current.length) return;
    let raf = 0, last: Rect | null = null;
    const tick = () => {
      const els = targets.current.filter((e) => e.isConnected);
      const r = els.length ? rectOf(els) : null;
      if (r && !same(last, r)) { last = r; setRect(r); }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [open, phase, step]);

  // prefetch the next section's code and data while this step is read
  useEffect(() => {
    if (!open || phase !== 'show') return;
    const next = STEPS[step + 1];
    if (!next?.path) return;
    preloadRoute(next.path);
    const pf = PREFETCH[next.path];
    if (pf && (!pf.feature || caps.can(pf.feature))) void qc.prefetchQuery({ queryKey: pf.key, queryFn: pf.fn, staleTime: Infinity });
  }, [open, phase, step, qc, caps]);

  useLayoutEffect(() => {
    const el = card.current;
    if (!el) return;
    if (el.classList.contains('dock')) { if (Math.abs(el.offsetHeight - dockH) > 1) setDockH(el.offsetHeight); }
    else if (Math.abs(el.offsetWidth - cardSize.w) > 1 || Math.abs(el.offsetHeight - cardSize.h) > 1) setCardSize({ w: el.offsetWidth, h: el.offsetHeight });
  });

  useEffect(() => {
    if (!open) return;
    document.body.classList.add('tour-open');
    const keys = (event: KeyboardEvent) => { if (event.key === 'Escape') void finish(startPath.current); };
    window.addEventListener('keydown', keys);
    return () => { document.body.classList.remove('tour-open'); window.removeEventListener('keydown', keys); };
  }, [open, finish]);
  useEffect(() => { if (open && phase === 'show') card.current?.querySelector<HTMLButtonElement>('.tour-next')?.focus({ preventScroll: true }); }, [open, phase, step]);

  if (!open) return null;
  const vw = window.innerWidth, vh = window.innerHeight;
  const showing = phase === 'show';
  const found = showing && !!rect && !last;

  // the lit area: the element + a little air, clipped to the screen; a tall element keeps its top part
  const lit = (floor: number): Rect | null => {
    if (!found || !rect) return null;
    // a tall element keeps a focus region at its top (never a light over most of the screen)
    const top = Math.max(8, rect.top - PAD), bottom = Math.min(floor, rect.top + rect.height + PAD, top + vh * 0.6);
    const left = Math.max(6, rect.left - PAD), right = Math.min(vw - 6, rect.left + rect.width + PAD);
    return bottom - top > 24 && right - left > 24 ? { left, top, width: right - left, height: bottom - top } : null;
  };
  // phone: the card docks at the bottom; a target that stays in the lower half gets it at the top
  const topDock = mobile && found && !!rect && rect.top + rect.height / 2 > vh / 2;
  const litPhone = (): Rect | null => {
    const r = lit(vh - 8);
    if (!r) return null;
    const ceil = topDock ? dockH + 24 : 0, floor = topDock ? vh - 8 : vh - dockH - 22;
    const top = Math.max(r.top, ceil + 8), bottom = Math.min(r.top + r.height, floor);
    return bottom - top > 24 ? { ...r, top, height: bottom - top } : null;
  };
  let spot = mobile ? litPhone() : lit(vh - 8);
  let dock = mobile;
  let cardStyle: CSSProperties | undefined;
  if (!mobile && found && spot) {
    const { w, h } = cardSize, gap = 16, m = 12;
    const fits = (x: number, y: number) => x >= m && y >= m && x + w <= vw - m && y + h <= vh - m;
    const cx0 = Math.min(vw - w - m, Math.max(m, spot.left + spot.width / 2 - w / 2));
    const cy0 = Math.min(vh - h - m, Math.max(m, spot.top + spot.height / 2 - h / 2));
    const s0 = spot;
    const place = ([[cx0, s0.top + s0.height + gap], [s0.left + s0.width + gap, cy0], [s0.left - w - gap, cy0], [cx0, s0.top - h - gap]] as Array<[number, number]>)
      .find(([x, y]) => fits(x, y));
    if (place) cardStyle = { left: place[0], top: place[1] };
    else { dock = true; spot = lit(vh - dockH - 28); }
  }
  // while moving, the card stays exactly where it was; its new place is taken only with the new step
  let placeDock = dock && !last, placeTop = topDock, placeCenter = !found && showing && !last;
  if (showing) lastPlace.current = { style: cardStyle, dock: placeDock, topDock, center: placeCenter };
  else ({ style: cardStyle, dock: placeDock, topDock: placeTop, center: placeCenter } = lastPlace.current);
  // while moving, the light closes to a point at the middle of where it was
  const shut = (r: Rect | null): CSSProperties => (r
    ? { left: r.left + r.width / 2, top: r.top + r.height / 2, width: 0, height: 0 }
    : { left: vw / 2, top: vh / 2, width: 0, height: 0 });

  const key = def.key;
  const vars = { lock: def.feature ? lockLine(caps.need(def.feature)) : '', count: plan.aiDailyLimit, plan: caps.planName };
  const textKey = locked ? `steps.${key}.locked`
    : key === 'plans' ? `steps.plans.${caps.plan === 'free' ? (caps.trialAvailable ? 'free' : 'freeUsed') : caps.plan}`
      : `steps.${key}.text`;
  const titleKey = key === 'plans' ? 'steps.plans.title' : `steps.${key}.title`;

  return (
    <div className={cx('tour-layer', !showing && 'moving')} role="dialog" aria-modal="true" aria-label={t('dialog')} data-step={key} data-phase={phase}>
      <div className="tour-block" />
      <div className="tour-veil" />
      <div className={cx('tour-spot', !(showing && spot) && 'shut')} style={showing && spot ? spot : shut(spot ?? rect)} />
      <section ref={card} className={cx('tour-card', last && showing && 'final', placeCenter && 'center', placeDock && 'dock', mobile && 'm', placeTop && 'dock-top')} style={cardStyle} aria-live="polite" aria-busy={!showing}>
        <div className="tour-brand">
          <img src="/assets/brand/orca-mark.png" alt="" />
          <span>{t('progress', { current: step + 1, total: STEPS.length })}</span>
        </div>
        <div className="tour-copy" key={step}>
          {locked && <span className="tour-lock">{vars.lock}</span>}
          <h2>{t(titleKey, vars)}</h2>
          <p>{t(textKey, vars)}</p>
          {slow && !showing && <span className="tour-wait">{t('opening')}</span>}
        </div>
        <div className="tour-progress" aria-hidden="true">{STEPS.map((x, i) => <i key={x.key} className={i <= step ? 'on' : ''} />)}</div>
        <div className="tour-actions">
          {!last && <button type="button" className="tour-skip" onClick={() => void finish(startPath.current)}>{t('skip')}</button>}
          <span />
          {step > 0 && <button type="button" className="tour-back" disabled={!showing} onClick={() => go(step - 1)}>{t('back')}</button>}
          <button type="button" className="tour-next" disabled={!showing} onClick={() => (last ? void finish('/') : go(step + 1))}>{last ? t('start') : t('next')}</button>
        </div>
      </section>
    </div>
  );
}
